import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { library, type LibraryEntry } from "@/data/aiWorkflows";
import {
  buildPromptLibraryOfferUrl,
  type LibraryCTAPlacement,
} from "@/utils/promptLibraryConversion";
import { trackEvent } from "@/utils/posthog";
import { buildOrganizationJourneyUrl } from "@/utils/organizationJourney";
import { cn } from "@/lib/utils";

export function PromptLibraryAI30Link({
  placement,
  workflow,
  children = "Explore AI 30 for your team",
  className,
}: {
  placement: LibraryCTAPlacement;
  workflow?: LibraryEntry;
  children?: ReactNode;
  className?: string;
}) {
  const search = useHydratedSearch();
  const { pathname } = useLocation();
  const selected =
    workflow ??
    library.find((entry) =>
      pathname.replace(/\/$/, "").endsWith(`/${entry.slug}`),
    );
  return (
    <Button
      asChild
      className={cn(
        "h-auto min-h-11 whitespace-normal bg-longhand-accent text-longhand-paper hover:bg-longhand-accent-hover",
        className,
      )}
    >
      <a
        href={buildPromptLibraryOfferUrl(search, placement, selected?.slug)}
        data-ai30-cta={placement}
        data-team-cta={placement}
        onClick={() =>
          trackEvent("Prompt Library AI 30 CTA Clicked", {
            audience: "organization",
            source_page: "fortune-100-prompt-library",
            cta_location: placement,
            workflow_slug: selected?.slug || "library",
          })
        }
      >
        {children}
      </a>
    </Button>
  );
}

export function PromptLibraryAI30Invitation({
  workflow,
}: {
  workflow?: LibraryEntry;
}) {
  const Heading = workflow ? "h3" : "h2";
  return (
    <aside
      aria-label="Put AI to work with your team"
      className="my-7 border-y border-longhand-border bg-longhand-accent-light px-5 py-6 sm:px-6"
    >
      <Heading className="font-serif text-xl font-semibold sm:text-2xl">
        {workflow ? `Help your team use ${workflow.title}.` : "Help your team put these workflows into practice."}
      </Heading>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-longhand-ink/80">
        In AI 30, your team builds and reviews useful workflows over 30 days, measures the results, and practices helping colleagues use them. Bring an existing workflow or start fresh. Smaller cohorts are welcome.
      </p>
      <div className="mt-4">
        <PromptLibraryAI30Link
          placement={workflow ? "workflow" : "starter"}
          workflow={workflow}
        >
          {workflow ? "Build this with your team" : "Explore AI 30 for your team"}
          <ArrowRight className="ml-2 h-4 w-4 shrink-0" aria-hidden="true" />
        </PromptLibraryAI30Link>
      </div>
    </aside>
  );
}

export function PromptLibraryAI30Offer() {
  const search = useHydratedSearch();
  const implementationHref = buildOrganizationJourneyUrl("/ai-call", search, { source: "fortune-100-prompt-library", location: "footer_implementation", audience: "organization" });
  return (
    <section
      aria-labelledby="library-ai30-title"
      className="border-t border-longhand-border px-4 py-10 sm:px-6 lg:px-8"
    >
      <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-longhand-accent">
            Put AI to work with your team
          </p>
          <h2
            id="library-ai30-title"
            className="mt-3 max-w-2xl font-serif text-3xl font-semibold"
          >
            Turn a useful workflow into a team capability.
          </h2>
          <ul className="mt-5 list-disc space-y-3 pl-5 text-sm leading-relaxed text-longhand-ink/80">
            <li>
              Try the workflow with your own notes or documents.
            </li>
            <li>
              Save your corrections and check whether the next run improves.
            </li>
            <li>
              Connect useful steps and compare the results with your starting
              point.
            </li>
          </ul>
          <div className="mt-5">
            <PromptLibraryAI30Link placement="footer">
              Put AI to work with your team
              <ArrowRight
                className="ml-2 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
            </PromptLibraryAI30Link>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-longhand-ink/80">
            Need help choosing seats and priorities for your cohort? <a className="inline-flex min-h-11 items-center font-semibold text-longhand-accent underline" href={implementationHref}>Discuss your team’s next step</a>.
          </p>
        </div>
        <figure className="self-center border-t border-longhand-border pt-6 md:border-t-0 md:pt-0">
          <blockquote className="font-serif text-xl leading-relaxed">
            “Tech Leaders will meet you where you are with patience,
            understanding, and real practical help.”
          </blockquote>
          <figcaption className="mt-4 text-sm text-longhand-ink/80">
            <strong className="text-longhand-ink">Pamela Johnston</strong>
            <br />
            Senior Business Strategy Consultant
            <br />
            Feedback from the Tech Leaders community
          </figcaption>
          <a
            className="mt-3 inline-flex min-h-11 items-center text-sm text-longhand-accent underline"
            href="https://embed-v2.testimonial.to/carousel/all/tech-leaders-mastermind-program?id=948cd5e8-1b47-43b4-8da1-a15b4bbdfb8e"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read the full feedback
          </a>
        </figure>
      </div>
    </section>
  );
}
